import {isServer} from 'coreUtils/utils';

interface Listener {
	(data: any): void;
	error?(err: Error): void;
}

type TListenersMap = Map<string, Listener[]>;
type TIdListenersMap = Map<string, TListenersMap>;

/**
 * Represents NamespaceEventManager
 * A simple event manager that allows for namespacing of events.
 * The structure of the storage is as follows:
 * {
 *     namespaceOne: {
 *         // Root level
 *         eventOne: [() => {}], // getAll
 *
 *         // Nested level
 *         eventTwo: {
 *             idOne: [() => {}], // getOne
 *         },
 *     },
 * }
 *
 * Usage:
 * Emit an event to all listeners of a specific action:
 * this.categoriesEvents.emit("someAction", { data: "someData" });
 *
 * Emit an event to all listeners of a specific action and ID:
 * this.categoriesEvents.emit("someAction", { data: "someData" }, "someId");
 *
 * Check the README.md for more information.
 */
class NamespaceEventManager {
	private listeners: TListenersMap = new Map();
	private idListeners: TIdListenersMap = new Map();

	/**
	 * Subscribe for updates
	 *
	 * @param {string} action - action
	 * @param {Listener} listener - listener
	 * @param {string} id - id
	 * @returns {Function} - off
	 */
	public on(action: string, listener: Listener, id?: string): () => void {
		if (id) {
			this.addIdListener(action, listener, id);
		} else {
			this.addGeneralListener(action, listener);
		}

		// Return the 'off' function
		return this.off.bind(this, action, listener, id);
	}

	/**
	 * Subscribe for updates that will be called only once
	 *
	 * @param {string} action - action
	 * @param {Listener} listener - listener
	 * @param {string} id - id
	 * @returns {void}
	 */
	public once(action: string, listener: Listener, id?: string): void {
		const onceListener = (data: any) => {
			listener(data);
			this.off(action, onceListener, id);
		};

		this.on(action, onceListener, id);
	}

	/**
	 * Unsubscribe from updates
	 *
	 * @param {string} action - action
	 * @param {Listener} listenerToRemove - listenerToRemove
	 * @param {string} id - id
	 * @returns {void}
	 */
	public off(action: string, listener: Listener, id?: string): void {
		const listenersArray = this.getListeners(action, id);

		if (listenersArray) {
			const index = listenersArray.indexOf(listener);

			if (index !== -1) {
				listenersArray.splice(index, 1);
			}
		}
	}

	/**
	 * Unsubscribe all listeners from updates
	 *
	 * @param {string} action - action
	 */
	public clearAll(action?: string): void {
		if (action) {
			this.listeners.delete(action);
			this.idListeners.delete(action);
		} else {
			this.listeners.clear();
			this.idListeners.clear();
		}
	}

	/**
	 * Gets the number of listeners for a specific action and ID
	 *
	 * @param {string} action - action
	 * @param {string} id - id
	 * @returns {number} - listener count
	 */
	public listenerCount(action: string, id?: string): number {
		const listenersArray = this.getListeners(action, id);

		return listenersArray ? listenersArray.length : 0;
	}

	/**
	 * Emits an event to all listeners of a specific action and/or ID synchronously
	 *
	 * @param {string} action - action
	 * @param {Listener} listenerToRemove - listenerToRemove
	 * @param {string} id - id
	 * @returns {void}
	 */
	public emitSync(action: string, data: any, id?: string): void {
		// Listeners will be notified only on the client side
		if (isServer) {
			return;
		}

		this._emit(action, data, id).catch((err) => {
			console.error('🔥 unexpected error in listener:', err);
		});
	}

	/**
	 * Emits an event to all listeners of a specific action and/or ID asynchronously
	 *
	 * @param {string} action - action
	 * @param {Listener} listenerToRemove - listenerToRemove
	 * @param {string} id - id
	 * @returns {Promise<void>}
	 */
	public async emitAsync(action: string, data: any, id?: string): Promise<void> {
		// Listeners will be notified only on the client side
		if (isServer) {
			return;
		}

		await this._emit(action, data, id);
	}

	/**
	 * The base emit method that is used by emitSync and emitAsync
	 *
	 * @param {string} action - action
	 * @param {any} data - data
	 * @param {string} id - id
	 * @returns {void}
	 */
	private async _emit(action: string, data: any, id?: string): Promise<void[]> {
		const immutableData = Object.freeze(data);
		const listenersToNotify = this.getListeners(action, id);

		// Go out in silence if there are no listeners
		if (!listenersToNotify) {
			return;
		}

		// Map listeners to their executions
		const notifications = listenersToNotify.map((listener) => {
			try {
				const result = listener(immutableData) as any;

				// If it returns a promise, then await it
				if (result instanceof Promise) {
					return result;
				}
			} catch (err) {
				// If the listener has an error method, call it.
				if (listener.error) {
					listener.error(err);
				}
			}
		});

		// If it's emitAsync, wait for all listeners to finish. If not, it doesn't wait.
		return Promise.all(notifications);
	}

	/**
	 * Adds a general listener, root level
	 * This method is responsible for adding listeners to the root level of the storage structure.
	 *
	 * @param {string} action - action
	 * @param {Listener} listener - listener
	 * @returns {void}
	 */
	private addGeneralListener(action: string, listener: Listener): void {
		if (!this.listeners.has(action)) {
			this.listeners.set(action, []);
		}

		const actionListeners = this.listeners.get(action);

		actionListeners.push(listener);
	}

	/**
	 * Adds a listener for a specific ID, nested level
	 * This method is responsible for adding listeners to the nested level of the storage structure.
	 *
	 * @param {string} action - action
	 * @param {Listener} listener - listener
	 * @param {string} id - id
	 * @returns {void}
	 */
	private addIdListener(action: string, listener: Listener, id: string): void {
		if (!this.idListeners.has(action)) {
			this.idListeners.set(action, new Map());
		}

		const idMap = this.idListeners.get(action);

		if (!idMap.has(id)) {
			idMap.set(id, []);
		}

		const idListeners = idMap.get(id);

		idListeners.push(listener);
	}

	/**
	 * Gets listeners for a specific action and ID
	 *
	 * @param {string} action - action
	 * @param {string} id - id
	 * @returns {Listener[] | undefined} - listeners
	 */
	private getListeners(action: string, id?: string): Listener[] | undefined {
		if (id) {
			const idMap = this.idListeners.get(action);

			return idMap && idMap.get(id);
		} else {
			return this.listeners.get(action);
		}
	}
}

export default NamespaceEventManager;
